import { Buffer } from 'buffer';
import axios from '@/axios';
import router from '@/router';
import { authService } from '@/services/auth';
import { $settings } from '@/provider/settings';
import localStorageHelper from '@/helper/localStorageHelper';
import { isEmpty } from '@/helper/lodashHelper';
import { deepReplaceObjectValue } from '@/helper/deepReplaceObjectValue';
import { isInOffice } from '@/utils/isInOffice';
import { useRootStore } from '@/store/root';
import { useOutlookStore } from '@/store/outlook';

const sapUrl = $settings.middlewareSAPProxyURI;
const url = $settings.middlewareURI;

const dataUrlToFile = async (
  dataUrl,
  fileName,
  dataType,
  stringify = false,
) => {
  const buff = stringify
    ? Buffer.from(dataUrl, 'utf8')
    : Buffer.from(dataUrl, 'base64');
  return new File([buff], fileName, { type: dataType });
};

const getMailId = () =>
  window.Office.context.mailbox.convertToRestId(
    window.Office.context.mailbox.item.itemId,
    window.Office.MailboxEnums.RestVersion.v2_0,
  ) || '';

const getAttachmentId = (attachment) =>
  window.Office.context.mailbox.convertToRestId(
    attachment.id,
    window.Office.MailboxEnums.RestVersion.v2_0,
  );

export default {
  async getObject({ commit }, { key, type }) {
    try {
      const response = await axios({ url: [sapUrl, type, key].join('/') });
      const { SUCCESS, DATA } = response.data;

      if (!SUCCESS || !DATA) router.push({ name: 'error-404' });

      deepReplaceObjectValue(DATA, '00:00:00', null);

      commit('SET_OBJECT', DATA);
    } catch (error) {
      console.warn(`Could not load partners data:`, error);
      const redirect = error.status === 403 ? 'error-403' : 'error-404';
      router.push({ name: redirect });
    }
  },
  async resetObject({ commit }) {
    return commit('SET_OBJECT', {});
  },
  async getForEdit(store, { type, key, method }) {
    const useType = type || useRootStore().parentObject.type;

    const getUpdateResponse = async () => {
      const response = await axios({
        url: `${[sapUrl, useType, key].join('/')}?ForEdit=X`,
      });

      const object = response.data;
      // Todo: BE should provide full structure
      if (type === 'opportunity' && object.DATA.DEBOPP_TEXTS.length === 0) {
        object.DATA.DEBOPP_TEXTS.push({
          MODIFY_FLAG: '',
          TDOBJECT: '',
          TDID: '',
          TDNAME: '',
          TDSPRAS: '',
          DESCR: '',
          TEXTSTRING: '',
        });
      }

      return object;
    };

    const getCopyResponse = async () => {
      const response = await axios({
        url: `${[sapUrl, useType, 'copy', key].join('/')}`,
      });

      return (response && response.data) || {};
    };

    const object =
      method === 'copy' ? await getCopyResponse() : await getUpdateResponse();

    store.dispatch('setEditObject', object.DATA);
  },
  async initObject(store, parameter) {
    const { type } = store.state;

    try {
      const response = await axios({
        method: 'POST',
        url: [sapUrl, type, 'init'].join('/'),
        data: parameter || {},
      });

      if (response && response.data) {
        const object = response.data;

        store.dispatch('setEditObject', object.DATA);

        if (!response.data.SUCCESS) {
          throw new Error(`Error: ${response.data.MESSAGES}`);
        }
      }
    } catch (error) {
      throw new Error(error);
    }
  },
  setEditObject(store, object) {
    return store.commit('SET_EDIT_OBJECT', object);
  },
  setEditInformation(store, information) {
    return store.commit('SET_EDIT_INFORMATION', information);
  },
  resetEditInformation(store) {
    return store.commit('RESET_EDIT_INFORMATION');
  },
  async getSchemaForBO({ state, commit, rootGetters }) {
    const { type } = state;
    const schema = rootGetters[`${type}/getSchema`];

    if (isEmpty(schema)) {
      try {
        const response = await axios({
          url: [sapUrl, type, 'schema'].join('/'),
        });

        const { components = {} } = response.data;

        commit('SET_SCHEMA', components);
      } catch (error) {
        throw new Error(error);
      }
    }
  },
  async augmentDataWithBearer(store, { value }) {
    const token = await authService.getGraphToken();

    Object.keys(value).some((recordBind) => {
      if ('BEARER_TOKEN' in value[recordBind]) {
        value[recordBind].BEARER_TOKEN = token.accessToken;
        return true;
      }
      return false;
    });

    return value;
  },
  async updateObject(store, { type, key, value }) {
    return axios({
      method: 'PUT',
      url: [sapUrl, type, key].join('/'),
      data: await store.dispatch('augmentDataWithBearer', { value }),
    }).then((response) => {
      store.dispatch('getObject', { key, type });
      return response.data.OBJKEY;
    });
  },
  async createObject(store, item) {
    return axios({
      method: 'POST',
      url: [sapUrl, item.type].join('/'),
      data: await store.dispatch('augmentDataWithBearer', item),
    }).then(async (response) => {
      if (isInOffice()) {
        const { OBJKEY, OBJTYPE } = response.data;
        await store
          .dispatch('addMailAttachmentsToBO', {
            key: OBJKEY,
            type: OBJTYPE,
          })
          .catch((error) => {
            console.error(error);
          });
      }

      return response.data.OBJKEY;
    });
  },
  async fetchProgressBar(store) {
    const { type, key } = useRootStore().getParentObject;
    const response = await axios({
      url: `${[sapUrl, type, 'progressbar', key].join('/')}`,
    });

    if (response && response.data) {
      return store.commit('SET_PROGRESS_BAR', response.data.DATA);
    }

    return Promise.reject;
  },
  async attachEMLToBO(store, { key, type }) {
    const mailID = getMailId();
    const requestUrl = `${[url, 'email', 'emlFile'].join(
      '/',
    )}?mailID=${mailID}`;

    const response = await axios.get(requestUrl);
    const { data } = response;

    const attachmentFile = await dataUrlToFile(
      data,
      'EML_attachment.eml',
      'application/octet-stream',
      true,
    );

    await store.dispatch('upload', {
      objKey: key,
      file: attachmentFile,
      type,
    });
  },
  async addMailAttachmentsToBO(store, { key, type }) {
    const attachments = store.getters.getAttachments;
    const isMail = useOutlookStore().getIsEmailView;
    const mailID = getMailId();
    const attachmentsArray = [];

    await Promise.all(
      attachments.map(async (attachment) => {
        const attachmentID = getAttachmentId(attachment) || attachment.id;

        const requestUrl = isMail
          ? `${[url, 'email', 'attachments'].join(
              '/',
            )}?mailID=${mailID}&attachmentID=${attachmentID}`
          : `${[url, 'email', 'event-attachments'].join(
              '/',
            )}?eventID=${mailID}&attachmentID=${attachmentID}`;

        const response = await axios.get(requestUrl);
        const { data } = response;
        const attachmentFile = await dataUrlToFile(
          data.contentBytes,
          data.name,
          data.contentType,
        );

        await store.dispatch('upload', {
          objKey: key,
          file: attachmentFile,
          type,
        });

        attachmentsArray.push(attachmentFile);
      }),
    );

    return attachmentsArray;
  },
  async getSuggestions({ state, dispatch }, { source, params }) {
    const response = await axios({
      method: 'post',
      url: [sapUrl, state.type, 'autosuggest'].join('/'),
      data: { SOURCE: source, REFS: params || [] },
    });
    dispatch('setSuggestions', response.data);
    return response.data || [];
  },
  setSuggestions(store, suggestions) {
    return store.commit('SET_SUGGESTIONS_PARTNERS', suggestions);
  },
  setAttachments({ commit }, attachments) {
    commit('SET_ATTACHMENTS', attachments);
  },
  init(store, { meta, type }) {
    store.commit('SET_TYPE', type);
    store.commit('SET_LISTS', meta.lists);
    store.commit('SET_OVERVIEW_BAR', meta.overviewBar);
    store.commit('SET_TITLE_PATTERN', meta.titlePattern);
    store.commit('SET_TILE_PATTERN', meta.tilePattern);
    store.commit('SET_TILE_FIELDS', meta.tileFields);
    store.commit('SET_AREA_PATTERN', meta.areaPattern);
    store.commit('SET_AREA_FIELDS', meta.areaFields);
    store.commit('SET_SUGGESTIONS', meta.autoSuggest);
    store.commit('SET_GROUPING', meta.tileGrouping);
    store.commit('SET_VIEW_AREAS', meta.viewAreas);
    store.commit('SET_KEY_FIELDS', meta.keyFields);
    store.commit('SET_SEARCH_CRITERIA', meta.searchCriteria);
    store.commit('SET_SEARCH_LISTS', meta.searchLists);
    store.commit('SET_SEARCH_AUTO_SUGGEST', meta.searchAutoSuggest);
    store.commit('SET_LIST_PAGE_SIZE', meta.pageSize);
    store.commit('SET_SETTINGS', meta.general);
    store.commit('SET_GLOBAL_SEARCH', meta.globalSearch);
    store.commit('SET_TEXTS', meta.texts);
    store.commit('SET_EXP_SEARCH', meta.expSearch);
  },
  async upload(store, { objKey, file, parent, type, subType }) {
    const formData = new FormData();
    const cid = localStorageHelper.getCid();

    formData.append('filename', file);

    if (parent) {
      for (const [key, value] of Object.entries(parent)) {
        formData.append(key, value);
      }
    }

    const {
      data: { MESSAGES, SUCCESS, DATA },
    } = await axios.post(
      [sapUrl, cid, type, 'upload', objKey].join('/'),
      formData,
      {
        params: { subType },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

    const errorMessages = MESSAGES.find(({ TYPE }) => TYPE === 'E');

    if (!SUCCESS || errorMessages) throw errorMessages;

    return DATA;
  },
};
